import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminApi from "../../api/adminApi";

// Fetch admin dashboard data
export const fetchAdminDashboard = createAsyncThunk(
  "admin/fetchDashboard",
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/dashboard");
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch dashboard"
      );
    }
  }
);

// Fetch users
export const fetchUsers = createAsyncThunk(
  "admin/fetchUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/users", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch users"
      );
    }
  }
);

// Fetch courses
export const fetchCourses = createAsyncThunk(
  "admin/fetchCourses",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/courses", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courses"
      );
    }
  }
);

// Fetch batches
export const fetchBatches = createAsyncThunk(
  "admin/fetchBatches",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/batches", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch batches"
      );
    }
  }
);

// Fetch enrollments
export const fetchEnrollments = createAsyncThunk(
  "admin/fetchEnrollments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/enrollments", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch enrollments"
      );
    }
  }
);

// Fetch payments
export const fetchPayments = createAsyncThunk(
  "admin/fetchPayments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/payments", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch payments"
      );
    }
  }
);

// Fetch enquiries
export const fetchEnquiries = createAsyncThunk(
  "admin/fetchEnquiries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/enquiries", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch enquiries"
      );
    }
  }
);

// Fetch queries
export const fetchQueries = createAsyncThunk(
  "admin/fetchQueries",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await adminApi.get("/queries", { params });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch queries"
      );
    }
  }
);

// Fetch single query
export const fetchQuery = createAsyncThunk(
  "admin/fetchQuery",
  async (queryId, { rejectWithValue }) => {
    try {
      const response = await adminApi.get(`/queries/${queryId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch query"
      );
    }
  }
);

const initialState = {
  dashboard: null,
  users: null,
  courses: null,
  batches: null,
  enrollments: null,
  payments: null,
  enquiries: null,
  queries: null,
  query: null,
  isLoading: false,
  error: null,
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearData: (state) => {
      state.dashboard = null;
      state.users = null;
      state.courses = null;
      state.batches = null;
      state.enrollments = null;
      state.payments = null;
      state.enquiries = null;
      state.queries = null;
      state.query = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard
      .addCase(fetchAdminDashboard.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboard.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dashboard = action.payload;
      })
      .addCase(fetchAdminDashboard.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Courses
      .addCase(fetchCourses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCourses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.courses = action.payload;
      })
      .addCase(fetchCourses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Batches
      .addCase(fetchBatches.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBatches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.batches = action.payload;
      })
      .addCase(fetchBatches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Enrollments
      .addCase(fetchEnrollments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEnrollments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.enrollments = action.payload;
      })
      .addCase(fetchEnrollments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Payments
      .addCase(fetchPayments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.payments = action.payload;
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Enquiries
      .addCase(fetchEnquiries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchEnquiries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.enquiries = action.payload;
      })
      .addCase(fetchEnquiries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Queries
      .addCase(fetchQueries.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQueries.fulfilled, (state, action) => {
        state.isLoading = false;
        state.queries = action.payload;
      })
      .addCase(fetchQueries.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Single Query
      .addCase(fetchQuery.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuery.fulfilled, (state, action) => {
        state.isLoading = false;
        state.query = action.payload;
      })
      .addCase(fetchQuery.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearData } = adminSlice.actions;
export default adminSlice.reducer;
